import { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Typography, Spin, Tag, List, Timeline } from 'antd';
import { Users, Building2, TrendingUp, Calendar, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { dashboardApi } from '../../services/api';

const { Title, Text } = Typography;

interface DashboardData {
  kpis: {
    totalLeads: number;
    newLeadsThisMonth: number;
    leadsGrowth: number;
    totalProperties: number;
    availableProperties: number;
    totalOpportunities: number;
    openOpportunities: number;
    pipelineValue: number;
    totalMeetings: number;
    upcomingMeetings: number;
  };
  charts: { leadsByStatus: any[]; opportunitiesByStage: any[] };
  recent: { leads: any[]; activities: any[] };
}

const statusColors: Record<string, string> = {
  NEW: 'blue', CONTACTED: 'cyan', QUALIFIED: 'green',
  NURTURING: 'orange', CONVERTED: 'purple', LOST: 'red',
};

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardApi.getOverview().then((r) => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;
  if (!data) return <Text type="danger">Error cargando dashboard</Text>;

  const { kpis, recent } = data;

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>Dashboard</Title>

      {/* KPIs */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Users size={14} /> Leads</span>}
              value={kpis.totalLeads}
              suffix={
                <span style={{ fontSize: 12, color: kpis.leadsGrowth >= 0 ? '#52c41a' : '#f5222d', display: 'flex', alignItems: 'center' }}>
                  {kpis.leadsGrowth >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {Math.abs(kpis.leadsGrowth)}%
                </span>
              }
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{kpis.newLeadsThisMonth} nuevos este mes</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Building2 size={14} /> Propiedades</span>}
              value={kpis.totalProperties}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{kpis.availableProperties} disponibles</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><TrendingUp size={14} /> Pipeline</span>}
              value={kpis.pipelineValue}
              prefix="€"
              precision={0}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{kpis.openOpportunities} oportunidades abiertas</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={14} /> Reuniones</span>}
              value={kpis.upcomingMeetings}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>{kpis.totalMeetings} en total</Text>
          </Card>
        </Col>
      </Row>

      {/* Recent activity */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Últimos leads" size="small">
            <List
              dataSource={recent.leads}
              renderItem={(lead: any) => (
                <List.Item>
                  <List.Item.Meta
                    title={`${lead.firstName} ${lead.lastName || ''}`}
                    description={new Date(lead.createdAt).toLocaleDateString('es-ES')}
                  />
                  <Tag color={statusColors[lead.status] || 'default'}>{lead.status}</Tag>
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Actividad reciente" size="small">
            <Timeline
              items={recent.activities.slice(0, 8).map((a: any) => ({
                children: (
                  <div>
                    <Text style={{ fontSize: 13 }}>{a.description}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {a.user?.name} · {new Date(a.createdAt).toLocaleDateString('es-ES')}
                    </Text>
                  </div>
                ),
              }))}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
